import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronRight, Mail, Phone } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/status-pill'
import { OrderStatusChips } from '@/components/order-status-chips'
import { ProductSummaryList } from '@/components/product-summary-list'
import { toProductSummaryItem } from '@/components/project-po-utils'
import { useProject, useProjects } from '@/api/hooks'
import { usePurchaseOrders } from '@/features/purchase-orders/hooks'
import { formatPhone } from '@/lib/phone'
import { parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'
import type { CustomerThread } from '@/features/customer-comms/thread-model'

type StatusChip = components['schemas']['StatusChip']

type PurchaseOrder = components['schemas']['PurchaseOrderListRead']
type ProjectCard = components['schemas']['InternalProjectCardRead']

const RECENT = 5
// Enough of the customer's newest POs to cover their recent projects.
const PO_LOOKUP = 30
const POS_PER_PROJECT = 3
const headingClass = 'text-xs font-medium uppercase tracking-wider text-muted-foreground'
const linkClass = 'font-medium text-primary hover:underline'

function shortDate(value: string | null | undefined) {
  return value ? parseUTCDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null
}

/** One PO: its number, supplier and where it stands. */
function PoRow({
  uuid,
  poNumber,
  manufacturer,
  order,
  shipping,
}: {
  uuid: string
  poNumber: string
  manufacturer?: string | null
  order?: StatusChip | null
  shipping?: StatusChip | null
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-1.5">
      <span className="min-w-0">
        <Link to="/purchase-orders/$poId" params={{ poId: uuid }} className={`block truncate font-mono text-xs ${linkClass}`}>
          {poNumber}
        </Link>
        {manufacturer && <span className="block truncate text-xs text-muted-foreground">{manufacturer}</span>}
      </span>
      <OrderStatusChips compact order={order} shipping={shipping} className="shrink-0 justify-end" />
    </li>
  )
}

/** A recent project: name and where it stands, then when it was placed and its POs, each a link. */
function RecentProjectRow({ project, pos }: { project: ProjectCard; pos: PurchaseOrder[] }) {
  const shown = pos.slice(0, POS_PER_PROJECT)
  return (
    <li className="space-y-0.5 py-1.5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/projects/$projectId" params={{ projectId: project.uuid }} className={`min-w-0 truncate text-sm ${linkClass}`}>
          {project.name}
        </Link>
        {project.status_summary && (
          <StatusPill tone={project.status_summary.headline.tone}>{project.status_summary.headline.label}</StatusPill>
        )}
      </div>
      <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
        <span>{shortDate(project.date_placed ?? project.created_at)}</span>
        {shown.map((po) => (
          <Link key={po.id} to="/purchase-orders/$poId" params={{ poId: po.uuid }} className={`font-mono ${linkClass}`}>
            {po.po_number}
          </Link>
        ))}
        {pos.length > shown.length && <span>+{pos.length - shown.length} more</span>}
      </p>
    </li>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1" aria-label={title}>
      <h3 className={headingClass}>{title}</h3>
      {children}
    </section>
  )
}

/** The project this conversation is about: its products and its POs, each a link (USA-1695). */
function ThreadProjectCard({ projectUuid }: { projectUuid: string }) {
  const { data: project, isLoading } = useProject(projectUuid)
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!project) return null
  const orders = project.orders ?? []
  const lineItems = orders.flatMap((order) => order.line_items ?? [])
  const pos = orders.flatMap((order) => order.purchase_orders ?? [])
  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <h3 className={headingClass}>Project</h3>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            to="/projects/$projectId"
            params={{ projectId: project.uuid }}
            className={`inline-flex min-w-0 items-center gap-0.5 break-words ${linkClass}`}
          >
            {project.name}
            <ChevronRight className="size-4 shrink-0" aria-hidden />
          </Link>
          {project.status_summary && (
            <StatusPill tone={project.status_summary.headline.tone}>{project.status_summary.headline.label}</StatusPill>
          )}
        </div>
        {lineItems.length > 0 && (
          <ProductSummaryList
            items={lineItems.map(toProductSummaryItem)}
            moreHref={{ to: '/projects/$projectId', params: { projectId: project.uuid } }}
          />
        )}
        {pos.length > 0 && (
          <ul className="divide-y border-t">
            {pos.map((po) => (
              <PoRow
                key={po.id}
                uuid={po.uuid}
                poNumber={po.po_number}
                manufacturer={po.manufacturer?.name}
                order={po.status_summary?.order}
                shipping={po.status_summary?.shipping}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/** Who the customer is, then their other recent projects and POs, each a link (USA-1695). */
function CustomerCard({
  group,
  email,
  phone,
  currentProjectUuid,
}: {
  group: NonNullable<CustomerThread['customer_group']>
  email?: string | null
  phone?: string | null
  currentProjectUuid?: string
}) {
  // One extra, since the conversation's own project is left out.
  const projects = useProjects({ customer_group_id: group.id, limit: RECENT + 1 })
  const pos = usePurchaseOrders({ customer_group_id: group.id, limit: PO_LOOKUP })
  const otherProjects = (projects.data?.items ?? [])
    .filter((project) => project.uuid !== currentProjectUuid)
    .slice(0, RECENT)
  const posByProject = new Map<string, PurchaseOrder[]>()
  for (const po of pos.data?.items ?? []) {
    const projectUuid = po.internal_order?.project?.uuid
    if (projectUuid) posByProject.set(projectUuid, [...(posByProject.get(projectUuid) ?? []), po])
  }
  const loading = projects.isLoading || pos.isLoading

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-4 p-4">
        <div className="space-y-1.5">
          <h3 className={headingClass}>Customer</h3>
          <Link to="/customers/$groupId" params={{ groupId: group.uuid }} className={`block break-words ${linkClass}`}>
            {group.name}
          </Link>
          {email && (
            <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
              <Mail className="size-3.5 shrink-0" aria-hidden />
              <span className="sr-only">Email</span>
              <span className="truncate">{email}</span>
            </p>
          )}
          {phone && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="size-3.5 shrink-0" aria-hidden />
              <span className="sr-only">Phone</span>
              {formatPhone(phone)}
            </p>
          )}
        </div>

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          otherProjects.length > 0 && (
            <Section title="Recent projects">
              <ul className="divide-y">
                {otherProjects.map((project) => (
                  <RecentProjectRow key={project.id} project={project} pos={posByProject.get(project.uuid) ?? []} />
                ))}
              </ul>
            </Section>
          )
        )}

        <nav className="flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-sm" aria-label="More for this customer">
          <Link to="/customers/$groupId" params={{ groupId: group.uuid }} search={{ tab: 'projects' }} className={linkClass}>
            All projects
          </Link>
          <Link to="/customers/$groupId" params={{ groupId: group.uuid }} search={{ tab: 'comms' }} className={linkClass}>
            All comms
          </Link>
        </nav>
      </CardContent>
    </Card>
  )
}

/** Beside a customer conversation: the project it is about, and the customer's recent projects and POs. */
export function CustomerContextPanel({ thread }: { thread: CustomerThread }) {
  return (
    <>
      {thread.project && <ThreadProjectCard projectUuid={thread.project.uuid} />}
      {thread.customer_group && (
        <CustomerCard
          group={thread.customer_group}
          email={thread.recipient_email ?? thread.customer_group.email}
          phone={thread.recipient_phone ?? thread.customer_group.phone}
          currentProjectUuid={thread.project?.uuid}
        />
      )}
    </>
  )
}
